import * as Sentry from '@sentry/react';
import { notificationManager } from './notificationManager';

let cleanupPromise = null;
let lastToastTime = 0;
const TOAST_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour

async function getStorageEstimate() {
  try {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const { quota, usage } = await navigator.storage.estimate();
      return {
        quotaMB: Math.round(quota / 1024 / 1024),
        usageMB: Math.round(usage / 1024 / 1024),
        usagePercent: quota ? Math.round((usage / quota) * 100) : null,
      };
    }
  } catch (_) {}
  return null;
}

/**
 * Detect quota-exceeded errors across browsers.
 * Covers Chrome/Firefox (QuotaExceededError), legacy Safari (code 22),
 * IndexedDB wrappers (.inner), and the exact Sentry message substring.
 */
export function isQuotaError(error) {
  if (!error) return false;

  if (error.name === 'QuotaExceededError') return true;
  if (error.code === 22) return true;

  const msg = (error.message || '').toLowerCase();
  if (msg.includes('quota')) return true;

  if (error.inner) return isQuotaError(error.inner);

  return false;
}

/**
 * Show a throttled warning toast (at most once per hour).
 */
function showQuotaToast() {
  const now = Date.now();
  if (now - lastToastTime < TOAST_COOLDOWN_MS) return;
  lastToastTime = now;

  notificationManager.showWarning(
    'Memorija uređaja je puna. Automatsko čišćenje je pokrenuto.',
    5,
  );
}

/**
 * Run cleanup in priority order to free IndexedDB space.
 * Uses dynamic imports to avoid circular dependencies.
 * Concurrent callers share the same cleanup promise so all
 * wait for cleanup to finish before retrying their writes.
 */
function performQuotaCleanup() {
  if (cleanupPromise) return cleanupPromise;

  cleanupPromise = (async () => {
    try {
      // 1. Image cache — largest store, 40% removal on force
      try {
        const { cleanupImageCache } = await import('./cacheUtils');
        await cleanupImageCache(true);
      } catch (_) {}

      // 2. Non-essential IndexedDB stores (HomeStatistics, ProductIndexes)
      try {
        const localforage = (await import('localforage')).default;
        const homeStats = localforage.createInstance({ name: 'HomeStatistics' });
        await homeStats.clear();
        const productIndexes = localforage.createInstance({ name: 'ProductIndexes' });
        await productIndexes.clear();
      } catch (_) {}

      // 3. Old receipts > 7 days
      try {
        const { cleanupOldReceipts } = await import('./receiptService');
        await cleanupOldReceipts(7);
      } catch (_) {}

      // 4. Sent order backups
      try {
        const { cleanupSentOrders } = await import('./localOrderBackup');
        await cleanupSentOrders();
      } catch (_) {}

      // 5. Expired localStorage data
      try {
        const { cleanupExpiredData } = await import('./storageCleanup');
        await cleanupExpiredData();
      } catch (_) {}
    } finally {
      cleanupPromise = null;
    }
  })();

  return cleanupPromise;
}

/**
 * Drop-in replacement for `store.setItem(key, value)`.
 *
 * 1. Attempts the write.
 * 2. On non-quota error → re-throws (don't mask real bugs).
 * 3. On quota error → toast, cleanup, retry once.
 * 4. If retry fails → logs to Sentry with fingerprint, returns false.
 * 5. On success → returns true.
 */
export async function safeSetItem(store, key, value) {
  try {
    await store.setItem(key, value);
    return true;
  } catch (error) {
    if (!isQuotaError(error)) throw error;

    const storeName = store._config?.name || 'unknown';
    const storeKey = String(key).substring(0, 80);
    const storageBefore = await getStorageEstimate();

    showQuotaToast();

    try {
      await performQuotaCleanup();
      await store.setItem(key, value);

      const storageAfter = await getStorageEstimate();
      Sentry.addBreadcrumb({
        category: 'storage',
        message: `Quota cleanup succeeded: ${storeName}/${storeKey}`,
        level: 'info',
        data: { storageBefore, storageAfter },
      });
      return true;
    } catch (retryError) {
      const storageAfter = await getStorageEstimate();
      Sentry.captureException(retryError, {
        tags: {
          errorType: 'quota-exceeded-after-cleanup',
          storeName,
          storeKey,
        },
        contexts: {
          storage: { before: storageBefore, after: storageAfter },
        },
        level: 'error',
        fingerprint: ['quota-exceeded-after-cleanup'],
      });
      return false;
    }
  }
}
